import {
  fetchCalendarUnavailabilityRequest,
  fetchCalendarViewCalendarEvents,
  fetchCalendarViewServiceRequests,
  fetchMultiBookingsListRequest,
  fetchRecentServiceRequests,
  fetchSingleCalendarEventRequest,
  fetchSingleServiceRequestRequest,
} from '@/services/api/serviceRequest';
import {selectCurrentJourneyId} from '@/models/journey';
import {adaptListResponse} from '@/utils/listParams';
import {trackModalView} from '@/services/tracker';
import {processAPIResponse} from '@/utils/request';
import {selectDateFilter} from '@/models/date';
import {openCSVLink} from '@/utils/download';
import get from 'lodash/get';
import {LEAD_VISIBILITY_SETTINGS_PLACES} from '@/components/Kanban/VisibilitySettings/constants';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import * as ServiceRequestSelectors from '@/selectors/serviceRequestSelectors';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import {
  processCalendarEvents,
  processServiceRequestsForCalendar,
} from '@/components/Kanban/ServiceRequests/Calendar/utils';
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import {updateEntityExtraField} from '@/utils/extraFields';
import { applyAppointmentDateFilter, APPOINTMENT_DATE_FILTER_TYPES } from '@/utils/exportFilters';
import { FILTER_PLACES } from '@/components/Filters/LeadFilters';


// Lead generation requests list: its own date range + appointment/creation switch (defaults to creation), not the global date.
export function selectJourneyServiceRequestsDateQuery(state) {
  const place = FILTER_PLACES.JOURNEY_SERVICE_REQUESTS;
  const date = PreferencesSelectors.selectFilters(state, place)?.date;
  const dateFilterType =
    PreferencesSelectors.selectPlacePreferences(state, place)?.dateFilterType?.value ??
    APPOINTMENT_DATE_FILTER_TYPES.CREATED;
  return applyAppointmentDateFilter({ date }, dateFilterType);
}

export function selectCurrentServiceRequestInfiniteList(state) {
  return state.serviceRequest.infiniteList;
}

export function selectServiceRequestJourneyId(state) {
  return state.serviceRequest.currentJourneyForServiceRequest;
}

const MAX_CALENDAR_EVENTS = 250;
export default {
  namespace: 'serviceRequest',

  state: {
    list: {},
    listParams: {},
    currentJourneyForServiceRequest: null,
    infiniteList: {},
    multiBookingsList: [],
    calendarView: {},
    createDrawer: {},
    detailsDrawer: {
      errors: {},
    },
    calendarEventDetailsDrawer: {
      errors: {},
    },
    calendarEvents: [],
    calendarUnavailabilities: [],
    details: {},
    calendarDate: new Date(),
    queriedCalendarDate: null,
    fetchCalendarViewAt: null,
  },

  effects: {
    *fetch(
      { payload = {} }: { payload: { search?: string; url?: string } },
      { call, put, select },
    ) {
      const date = yield select(selectJourneyServiceRequestsDateQuery);
      const journeyId = yield select(selectCurrentJourneyId);
      const storedParams = yield select((state: any) => state.serviceRequest.listParams);

      const search = 'search' in payload ? payload.search : storedParams?.search;
      if (search !== storedParams?.search) {
        yield put({ type: 'savedListParams', payload: { search } });
      }

      const query: Record<string, any> = { ...date };
      if (search) query.search = search;
      const { response, data } = yield call(fetchRecentServiceRequests, {
        params: { journeyId },
        url: payload.url,
        query,
      });
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(data) {
            yield put({
              type: 'saveRecentServiceRequests',
              payload: adaptListResponse(data),
            });
          },
        },
      );
    },
    *downloadExport(
      { payload = {} }: { payload: { url?: string } },
      { call, select },
    ) {
      const date = yield select(selectJourneyServiceRequestsDateQuery);
      const journeyId = yield select(selectCurrentJourneyId);
      const { response, data } = yield call(fetchRecentServiceRequests, {
        params: { journeyId },
        url: payload.url,
        query: { ...date },
      });
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(data) {
            if (data?.links?.export) {
              yield openCSVLink(data.links.export);
            }
          },
          *onError() {},
        }, 'data'
      );
    },
    *fetchInfinite({ payload }, { call, put, select }) {
      const date = yield select(selectDateFilter);
      const journeyId = yield select(selectCurrentJourneyId);
      const previousJourneyId = yield select(selectServiceRequestJourneyId);
      if (previousJourneyId !== journeyId) {
        payload.reset = true;
        yield put({
          type: 'saveJourney',
          payload: { journeyId },
        });
      }
      const { reset } = payload;
      let url = undefined;
      if (!reset) {
        const current = yield select(selectCurrentServiceRequestInfiniteList);
        url = current?.links?.next;
      }
      const { response, data } = yield call(fetchRecentServiceRequests, {
        params: { journeyId },
        url,
        query: { limit: 4, ...date },
      });
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(data) {
            yield put({
              type: 'saveInfiniteServiceRequests',
              payload: {
                data: data,
                ...payload,
              },
            });
          },
        },
        'data',
      );
    },
    *fetchSingleAppointment({ payload }, { call, put, select }) {
      const { appointmentId } = payload;

      const { response, data } = yield call(fetchSingleServiceRequestRequest, {
        params: { serviceRequestId: appointmentId },
      });
      yield processAPIResponse(
        { response, data },
        {
          showErrorMessage: true,
          *onSuccess(data) {
            yield put({
              type: 'setCalendarEvents',
              payload: {
                events: processServiceRequestsForCalendar([data]),
              },
            });
          },
          *onError(error) {
            yield put({
              type: 'saveDetailsDrawerState',
              payload: {
                errors: {
                  ...yield select((state: any) => state.serviceRequest.detailsDrawer.errors),
                  [appointmentId]: error,
                },
              },
            });
          },
        },
        'data',
      );
    },
    /**
     * Fetch today's service requests and calendar events for the upcoming
     * meeting notification chip. Results are merged into the shared
     * calendarEvents state without affecting calendar-view filters.
     */
    *fetchTodayAppointmentsForChip({ payload = {} }, { call, put }) {
      const today = dayjs().format('YYYY-MM-DD');
      const query = { start_date: today, end_date: today, page_size: 100, with: 'lead,agent' };

      try {
        const srResponse = yield call(fetchCalendarViewServiceRequests, { query });
        yield processAPIResponse(srResponse, {
          *onSuccess(data) {
            const events = processServiceRequestsForCalendar(data.items || data.data || []);
            yield put({ type: 'setCalendarEvents', payload: { events } });
          },
        });
      } catch (_) {}

      // Only fetch calendar events when the user is allowed to view them,
      // otherwise the request 403s and surfaces an error to the user.
      if (payload.canViewCalendarEvents) {
        try {
          const ceResponse = yield call(fetchCalendarViewCalendarEvents, { query });
          yield processAPIResponse(ceResponse, {
            *onSuccess(data) {
              const events = processCalendarEvents(data.items || data.data || data || []);
              yield put({ type: 'setCalendarEvents', payload: { events } });
            },
          }, 'data');
        } catch (_) {}
      }
    },
    *fetchSingleCalendarEvent({ payload }, { call, put, select }) {
      const { calendarEventId } = payload;

      const { response, data } = yield call(fetchSingleCalendarEventRequest, {
        params: { calendarEventId },
      });
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(data) {
            yield put({
              type: 'setCalendarEvents',
              payload: {
                events: processCalendarEvents([data]),
              },
            });
          },
          *onError(error) {
            yield put({
              type: 'saveCalendarEventDetailsDrawerState',
              payload: {
                errors: {
                  ...yield select((state: any) => state.serviceRequest.calendarEventDetailsDrawer.errors),
                  [calendarEventId]: error,
                },
              },
            });
          },
        },
        'data',
      );
    },
    *fetchMultiBookingsList(_, { call, put }) {
      const { response, data } = yield call(fetchMultiBookingsListRequest);
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(data) {
            yield put({
              type: 'saveMultiBookingsList',
              payload: data,
            });
          },
        },
        'data',
      );
    },
    fetchAppointmentList: [
      function* ({ payload = {} }, { call, put, select }) {
        const filters = yield select((state) =>
          PreferencesSelectors.selectFilters(state, 'APPOINTMENTS_TABLE'),
        );
        const preferences = yield select((state) =>
          PreferencesSelectors.selectPlacePreferences(
            state,
            'APPOINTMENTS_TABLE',
          ),
        );

        const shownProperties = yield select((state) =>
          PreferencesSelectors.selectShownProperties(
            state,
            LEAD_VISIBILITY_SETTINGS_PLACES.APPOINTMENT_TABLE,
          ),
        );
        const extraFieldsToShow = shownProperties
          .filter((property) => property.startsWith('extraField:'))
          .map((property) => property.split('extraField:')[1]);

        const filteredObj = Object.fromEntries(
          Object.entries(filters || {}).filter(
            ([key, value]) => value !== null,
          ),
        );

        filteredObj.page_size = window.innerHeight > 800 ? 50 : 30;

        if (!filteredObj.showInactiveEvents) {
          filteredObj.status = 'confirmed,tentative';
        }
        filteredObj.sort_by = 'utc_start_time';
        filteredObj.sort_direction = 'desc';

        applyAppointmentDateFilter(
          filteredObj,
          preferences?.dateFilterType?.value,
        );

        filteredObj.with = 'agent';

        if (!isEmpty(extraFieldsToShow)) {
          filteredObj.with_extra_fields = extraFieldsToShow.join(',');
        }

        const query = { ...filteredObj, ...omit(payload, ['url', 'date']) };
        const journeyId = yield select(selectCurrentJourneyId);

        const { response, data } = yield call(
          fetchCalendarViewServiceRequests,
          {
            params: { journeyId },
            query,
          },
        );

        yield processAPIResponse(
          { response, data },
          {
            *onSuccess(data) {
              const batchEvents = processServiceRequestsForCalendar(
                data.items || data.data,
              );

              // Dispatch Redux update for this batch
              yield put({
                type: 'setCalendarEvents',
                payload: {
                  events: batchEvents,
                },
              });

              yield put({
                type: 'saveAppointmentList',
                payload: data,
                options: query,
              });
            },
          },
        );
      },
      {
        type: 'takeLatest',
      },
    ],
    fetchCalendarViewEvents: [
      function* ({ payload = {} }, { call, put, select }) {
        const filters = yield select(
          PreferencesSelectors.selectCalendarFilters,
        );

        // region Date
        const dates = yield select(
          PreferencesSelectors.selectCalendarDateWindow,
        );
        const calendarDate = yield select(
          ServiceRequestSelectors.selectCalendarDate,
        );
        // endregion

        // region Extra Fields
        const shownCalendarEventPopoverProperties = yield select(
          PreferencesSelectors.selectShownCalendarEventPopoverProperties,
        );
        const shownCalendarEventProperties = yield select(
          PreferencesSelectors.selectShownCalendarEventCardProperties,
        );
        const allShownProperties = Array.from(
          new Set([
            ...shownCalendarEventPopoverProperties,
            ...shownCalendarEventProperties,
          ]),
        );
        const extraFieldsToShow = allShownProperties
          .filter((property) => property.startsWith('extraField:'))
          .map((property) => property.split('extraField:')[1]);
        // endregion

        // region Branch
        let selectedBranch = filters?.calendarBranch;
        if (!Array.isArray(selectedBranch)) selectedBranch = [selectedBranch];
        const isViewingNoBranch =
          selectedBranch.length === 1 && selectedBranch[0] === 'null';
        // endregion

        // Busy-elsewhere shading loads in parallel; it must never delay the
        // events fetch below (put is non-blocking, effect is takeLatest).
        yield put({ type: 'fetchCalendarUnavailability' });

        const query = {
          ...omit(filters, ['selectedBranch']),
          // all: 1,
          page_size: MAX_CALENDAR_EVENTS,
          page: 1,
          skip_total_size: 1,
          with: 'agent',
          branch: selectedBranch.join(','),
          start_date: dates[0],
          end_date: dates[1],
        };
        if (!isEmpty(extraFieldsToShow))
          query.with_extra_fields = extraFieldsToShow.join(',');
        if (isViewingNoBranch) query.branch = 'null';

        // region Request
        let events = [];

        // Fetch service requests with pagination
        try {
          let currentQuery = { ...query };
          let hasNextPage = true;

          while (hasNextPage) {
            const ServiceRequestAPIResponse = yield call(
              fetchCalendarViewServiceRequests,
              { query: currentQuery },
            );

            yield processAPIResponse(ServiceRequestAPIResponse, {
              *onSuccess(data) {
                const batchEvents = processServiceRequestsForCalendar(
                  data.items || data.data,
                );
                events = events.concat(batchEvents);

                yield put({
                  type: 'setCalendarEvents',
                  payload: {
                    events: batchEvents,
                  },
                });
              },
              *onError() {
                hasNextPage = false;
              },
            });

            const nextUrl = ServiceRequestAPIResponse.data?.links?.next;
            if (nextUrl) {
              const urlParams = new URLSearchParams(nextUrl.split('?')[1]);
              const nextPage = parseInt(urlParams.get('page')) || 2;
              currentQuery = { ...currentQuery, page: nextPage };
            } else {
              hasNextPage = false;
            }
          }
        } catch (error) {
          console.warn('Service request API call failed:', error);
        }

        // Fetch calendar events with pagination — only when the user can view
        // them, otherwise the request 403s and surfaces an error to the user.
        if (payload.canViewCalendarEvents) try {
          let currentQuery = { ...query };
          let hasNextPage = true;

          while (hasNextPage) {
            const CalendarEventsAPIResponse = yield call(
              fetchCalendarViewCalendarEvents,
              { query: currentQuery },
            );

            yield processAPIResponse(
              CalendarEventsAPIResponse,
              {
                *onSuccess(data) {
                  const batchEvents = processCalendarEvents(data.items || data.data || data);
                  events = events.concat(batchEvents);

                  yield put({
                    type: 'setCalendarEvents',
                    payload: {
                      events: batchEvents,
                    },
                  });
                },
                *onError(error) {
                  console.warn('Failed to fetch calendar events:', error);
                  hasNextPage = false;
                },
              },
              'data',
            );

            // Read links from the raw response body, before dataKey extraction stripped it.
            // Laravel's paginateEloquentQuery response nests one level deeper than legacy.
            const nextUrl = CalendarEventsAPIResponse.data?.links?.next || CalendarEventsAPIResponse.data?.data?.links?.next;
            if (nextUrl) {
              const urlParams = new URLSearchParams(nextUrl.split('?')[1]);
              const nextPage = parseInt(urlParams.get('page')) || 2;
              currentQuery = { ...currentQuery, page: nextPage };
            } else {
              hasNextPage = false;
            }
          }
        } catch (error) {
          console.warn('Calendar events API call failed:', error);
        }
        // endregion

        // region Save Events
        yield put({
          type: 'leads/saveLeadsData',
          payload: {
            list: events
              .map((e) => ({
                ...e?.lead,
                extraFields: e?.extraFields || [],
              }))
              .filter((e) => e?.id),
          },
        });
        // endregion

        yield put({
          type: 'setQueriedCalendarDate',
          payload: {
            date: calendarDate,
          },
        });
      },
      {
        type: 'takeLatest',
      },
    ],
    // Bookings of the filtered calendar's agents that sit on branch services
    // OUTSIDE the filter (GET /service-requests/calendar-unavailability) —
    // the busy-slot painter shows them like any locally-detected overlap.
    fetchCalendarUnavailability: [
      function* (_action, { call, put, select }) {
        const filters = yield select(
          PreferencesSelectors.selectCalendarFilters,
        );
        const dates = yield select(
          PreferencesSelectors.selectCalendarDateWindow,
        );

        let selectedBranch = filters?.calendarBranch;
        if (!Array.isArray(selectedBranch)) selectedBranch = [selectedBranch];
        const branchUuids = selectedBranch.filter(
          (branch) => branch && branch !== 'null',
        );

        if (isEmpty(branchUuids)) {
          yield put({
            type: 'setCalendarUnavailabilities',
            payload: { unavailabilities: [] },
          });
          return;
        }

        const query = {
          branch: branchUuids.join(','),
          start: dates[0],
          // `end` bounds utc_start_time inclusively — cover the last day.
          end: `${dates[1]} 23:59:59`,
        };
        // Narrows the branch services (and thus agents) considered, the same
        // way the events call is scoped to the active Service Type filter.
        if (!isEmpty(filters?.service_type))
          query.service_type = filters.service_type;

        const APIResponse = yield call(fetchCalendarUnavailabilityRequest, {
          query,
        });

        yield processAPIResponse(
          APIResponse,
          {
            *onSuccess(unavailabilities) {
              yield put({
                type: 'setCalendarUnavailabilities',
                payload: { unavailabilities: unavailabilities || [] },
              });
            },
            // Shading is auxiliary — a failure must never surface a toast.
            *onError(error) {
              console.warn('Calendar unavailability fetch failed:', error);
            },
          },
          'data',
        );
      },
      {
        type: 'takeLatest',
      },
    ],
  },
  reducers: {
    saveRecentServiceRequests(state, action) {
      return {
        ...state,
        list: action.payload,
      };
    },
    savedListParams(state, action) {
      return { ...state, listParams: { ...state.listParams, ...action.payload } };
    },
    saveAppointmentList(state, action) {
      let newList = action.payload;

      if (newList.current_page > 1 && Array.isArray(state?.list?.data)) {
        const existing = state.list.data;
        const combined = [...existing, ...newList.data];

        const mergedMap = new Map();
        for (const item of combined) {
          mergedMap.set(item.id, item);
        }

        newList = {
          ...newList,
          data: Array.from(mergedMap.values()),
        };
      }

      return {
        ...state,
        list: newList,
      };
    },
    saveJourney(state, action) {
      return {
        ...state,
        currentJourneyForServiceRequest: action.payload.journeyId,
      };
    },
    saveInfiniteServiceRequests(state, action) {
      const { payload } = action;
      const { data, reset } = payload;
      const items = reset
        ? data.items
        : state.infiniteList.items.concat(data.items);
      const newInfiniteList = {
        ...data,
        items,
      };
      return {
        ...state,
        infiniteList: newInfiniteList,
      };
    },
    saveMultiBookingsList(state, action) {
      return {
        ...state,
        multiBookingsList: action.payload,
      };
    },
    toggleCreateServiceRequestDrawer(state, action) {
      const { payload } = action;

      trackModalView('service-request-create');
      return {
        ...state,
        createDrawer: {
          ...(payload || {}),
          toggledAt: new Date().getTime(),
        },
      };
    },

    highlightServiceRequest(state, { payload }) {
      const serviceRequest = get(payload, 'serviceRequest');
      return {
        ...state,
        highlightedServiceRequest: serviceRequest,
      };
    },
    updateFetchCalendarViewAt(state) {
      return {
        ...state,
        fetchCalendarViewAt: Date.now(),
      };
    },
    toggleDrawer(state, action) {
      const {
        payload: { serviceRequest, nested },
      } = action;
      const drawer = state.drawer || {};
      drawer.nested = nested;
      drawer.toggledAt = new Date().getTime();
      if (serviceRequest) {
        drawer.serviceRequest = serviceRequest;
        drawer.visible = true;
        trackModalView('service-request-details');
      } else {
        drawer.visible = false;
      }

      return {
        ...state,
        drawer,
      };
    },
    toggleDetailsDrawer(state, action) {
      const appointmentId = action.payload?.appointmentId;
      const errors = {...(state.detailsDrawer?.errors || {})};
      if (appointmentId) {
        errors[appointmentId] = null;
      }

      return {
        ...state,
        detailsDrawer: {
          ...state.detailsDrawer,
          ...(action.payload || {}),
          toggledAt: new Date().getTime(),
          open: Boolean(appointmentId),
          errors,
        },
      };
    },
    saveDetailsDrawerState(state, action) {
      return {
        ...state,
        detailsDrawer: {
          ...state.detailsDrawer,
          ...(action.payload || {}),
        },
      };
    },
    toggleCalendarEventDetailsDrawer(state, action) {
      const calendarEventId = action.payload?.calendarEventId;
      const errors = {...(state.calendarEventDetailsDrawer?.errors || {})};
      if (calendarEventId) {
        errors[calendarEventId] = null;
      }

      return {
        ...state,
        calendarEventDetailsDrawer: {
          ...state.calendarEventDetailsDrawer,
          ...(action.payload || {}),
          toggledAt: new Date().getTime(),
          open: Boolean(calendarEventId),
          errors,
        },
      };
    },
    saveCalendarEventDetailsDrawerState(state, action) {
      return {
        ...state,
        calendarEventDetailsDrawer: {
          ...state.calendarEventDetailsDrawer,
          ...(action.payload || {}),
        },
      };
    },
    setCalendarDate(state, action) {
      return {
        ...state,
        calendarDate: action.payload.date,
      };
    },
    setQueriedCalendarDate(state, action) {
      return {
        ...state,
        queriedCalendarDate: action.payload.date,
      };
    },
    setCalendarEvents(state, action) {
      const newCalendarEvents = cloneDeep(state.calendarEvents || [])
      const details = cloneDeep(state.details || {});
      for (const newEvent of action.payload.events) {
        const index = newCalendarEvents.findIndex(
          (event) => event.id === newEvent.id,
        );
        if (index !== -1) {
          newCalendarEvents[index] = {
            ...newCalendarEvents[index],
            ...newEvent,
          };
        } else {
          newCalendarEvents.push(newEvent);
        }
        details[newEvent.id] = {
          ...(details?.[newEvent.id] || {}),
          ...newEvent,
        };
      }
      return {
        ...state,
        details,
        calendarEvents: newCalendarEvents,
      };
    },
    setCalendarUnavailabilities(state, action) {
      return {
        ...state,
        calendarUnavailabilities: action.payload.unavailabilities || [],
      };
    },
    updateServiceRequestExtraField(state, action) {
      const { serviceRequestId, extraFieldTypeId, value, fieldType } =
        action.payload;
      if (!serviceRequestId) return state;

      const serviceRequest = state.details?.[serviceRequestId] || {};
      const newServiceRequest = updateEntityExtraField(
        serviceRequest,
        extraFieldTypeId,
        value,
        fieldType,
      );

      const calendarEvents = state.calendarEvents.map((event) => {
        if (event.id === serviceRequestId) {
          return { ...event, ...newServiceRequest };
        }
        return event;
      });

      const list = state.list;
      const updatedListData = Array.isArray(list?.data)
        ? list.data.map((item) =>
            item.id === serviceRequestId ? { ...item, ...newServiceRequest } : item,
          )
        : list?.data;

      return {
        ...state,
        calendarEvents,
        list: updatedListData !== list?.data ? { ...list, data: updatedListData } : list,
        details: {
          ...(state.details || {}),
          [serviceRequestId]: newServiceRequest,
        },
      };
    },
    updateCalendarEvent(state, action) {
      const updatedEvent = action.payload.event;
      let found = false
      const calendarEvents = state.calendarEvents.map((event) => {
        if (event.id === updatedEvent.id) {
          found = true;
          return { ...event, ...updatedEvent };
        }
        return event;
      });
      if (!found)
        calendarEvents.push(updatedEvent);

      const list = state.list;
      const updatedListData = Array.isArray(list?.data)
        ? list.data.map((item) =>
            item.id === updatedEvent.id ? { ...item, ...updatedEvent } : item,
          )
        : list?.data;

      return {
        ...state,
        calendarEvents,
        list: updatedListData !== list?.data ? { ...list, data: updatedListData } : list,
        details: {
          ...(state.details || {}),
          [updatedEvent.id]: {
            ...(state.details?.[updatedEvent.id] || {}),
            ...updatedEvent,
          },
        },
      };
    },
    removeCalendarEvent(state, action) {
      const calendarEventId = action.payload?.calendarEventId;
      const calendarEvents = state.calendarEvents.filter((event) => {
        return event.id !== calendarEventId;
      });

      return {
        ...state,
        calendarEvents,
        details: {
          ...(state.details || {}),
          [calendarEventId]: undefined
        },
      };
    },
    // Drop every occurrence of a recurring series (recurring_deleted /
    // recurring_updated carry only the series uuid — never per-occurrence rows —
    // and setCalendarEvents only upserts, so a refetch alone can't remove them).
    removeRecurringCalendarEvents(state, action) {
      const recurringId = action.payload?.recurringId;
      if (!recurringId) return state;

      const details = { ...(state.details || {}) };
      const calendarEvents = state.calendarEvents.filter((event) => {
        if (event?.recurring?.id !== recurringId) return true;
        details[event.id] = undefined;
        return false;
      });

      return {
        ...state,
        calendarEvents,
        details,
      };
    },
  },
};
